import { ArrowRight, CheckCircle, MessageSquare } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkflowContext } from '../../../contexts/WorkflowContext';
import { IntegrationsNodeData } from '../../../types/nodes';
import { ConnectTargetStep } from './steps/ConnectTargetStep';
import { MessageFormatStep } from './steps/MessageFormatStep';
import { PreviewStep } from './steps/PreviewStep';
import { SendStep } from './steps/SendStep';

interface SlackWizardProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
	onClose?: () => void;
}

export interface SlackWizardConfig {
	// Step 1: Connect & Target
	credentialId: string;
	channels: string[];
	threading: {
		mode: 'none' | 'reply' | 'start_new';
		threadTs?: string;
		exposeTs?: boolean;
	};

	// Step 2: Message Format
	format: 'text' | 'markdown' | 'blocks';
	content: {
		text?: string;
		blocks?: any[];
	};

	// Step 3: Preview (computed)
	previewData?: {
		valid: boolean;
		rendered: any;
		errors?: string[];
	};

	// Step 4: Send & Outputs
	options: {
		unfurlLinks: boolean;
		unfurlMedia: boolean;
		linkNames: boolean;
	};
	outputs: {
		exposePermalinks: boolean;
		exposeChannelIds: boolean;
		exposeChannelNames: boolean;
	};
}

const WIZARD_STEPS = [
	{ id: 1, title: 'Connect & Target', icon: MessageSquare },
	{ id: 2, title: 'Message Format', icon: MessageSquare },
	{ id: 3, title: 'Preview', icon: CheckCircle },
	{ id: 4, title: 'Send & Outputs', icon: CheckCircle },
];

/**
 * Slack Integration Wizard Component
 *
 * @description Provides a 4-step wizard interface for configuring Slack message nodes
 * with bot token authentication, multi-channel support, and advanced features
 *
 * @param nodeId - Unique identifier for the workflow node
 * @param data - Current node data and configuration
 * @param onChange - Callback to update node data
 * @param onWizardStateChange - Callback to communicate wizard state to parent
 * @param onValidationRequest - Callback to provide validation function to parent
 * @param onClose - Callback when wizard is completed or closed
 */
export function SlackWizard({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
	onClose,
}: SlackWizardProps) {
	const [currentStep, setCurrentStep] = useState(1);
	const [wizardMode, setWizardMode] = useState(true);

	// Get available upstream fields for variable picker
	const { getAvailableFields, getFlattenedFields } = useWorkflowContext();

	const [config, setConfig] = useState<SlackWizardConfig>(() => {
		// Initialize from existing data or defaults
		const existingConfig = data.config as Partial<SlackWizardConfig>;

		// Initialize with existing config or empty state (no artificial defaults)
		return {
			credentialId: existingConfig.credentialId || 'default',
			channels: existingConfig.channels || [],
			threading: {
				mode: 'none',
				...existingConfig.threading,
			},
			format: existingConfig.format || 'text',
			content: {
				text: existingConfig.content?.text || '',
				blocks: existingConfig.content?.blocks || [],
			},
			options: {
				unfurlLinks: true,
				unfurlMedia: true,
				linkNames: true,
				...existingConfig.options,
			},
			outputs: {
				exposePermalinks: true,
				exposeChannelIds: true,
				exposeChannelNames: true,
				...existingConfig.outputs,
			},
		};
	});

	const [validationState, setValidationState] = useState({
		attempted: false,
		failed: false,
		errors: [] as string[],
	});

	// Add validation state management like other components
	const [validationAttempted, setValidationAttempted] = useState(false);
	const [validationFailed, setValidationFailed] = useState(false);

	// Update parent data only when user makes meaningful changes
	const updateParentData = useCallback(
		(newConfig: SlackWizardConfig) => {
			const updatedData: IntegrationsNodeData = {
				...data,
				config: {
					type: 'slack',
					...newConfig,
				},
			};
			onChange(updatedData);
		},
		[data, onChange]
	);

	// Validate current step
	const validateCurrentStep = useCallback(async (): Promise<boolean> => {
		const errors: string[] = [];

		switch (currentStep) {
			case 1: // Connect & Target
				if (!config.credentialId.trim()) {
					errors.push('Credential selection is required');
				}

				if (config.channels.length === 0) {
					errors.push('At least one channel is required');
				} else if (config.channels.some(c => !c || !c.trim())) {
					errors.push('All channels must have valid names');
				}
				if (config.threading.mode === 'reply' && !config.threading.threadTs?.trim()) {
					errors.push('Thread timestamp is required for reply mode');
				}
				break;

			case 2: // Message Format
				switch (config.format) {
					case 'text':
					case 'markdown':
						if (!config.content.text?.trim()) {
							errors.push(`${config.format} format requires text content`);
						}
						break;
					case 'blocks':
						if (!config.content.blocks || config.content.blocks.length === 0) {
							errors.push('Block Kit format requires blocks array');
						}
						break;
				}
				break;

			case 3: // Preview
				// Preview validation happens in the preview step component
				break;

			case 4: // Send & Outputs
				// Final validation - all previous steps should be valid
				break;
		}

		const isValid = errors.length === 0;
		setValidationState({
			attempted: true,
			failed: !isValid,
			errors,
		});

		return isValid;
	}, [currentStep, config]);

	// Navigation handlers - memoized to prevent re-renders
	const handlePreviousStep = useCallback(() => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	}, [currentStep]);

	const handleNextStep = useCallback(async () => {
		const isValid = await validateCurrentStep();
		if (isValid && currentStep < WIZARD_STEPS.length) {
			setCurrentStep(currentStep + 1);
		}
	}, [currentStep, validateCurrentStep]);

	const handleCompleteWizard = useCallback(async () => {
		// Step 1: If validation hasn't been attempted yet, run validation and stay in node
		if (!validationAttempted) {
			const isValid = await validateCurrentStep();
			setValidationAttempted(true);

			if (!isValid) {
				// Validation failed - stay in node and show errors
				setValidationFailed(true);
				return; // Don't exit the node
			} else {
				// Validation succeeded - proceed to exit
				setValidationFailed(false);
			}
		}

		// Step 2: If validation was already attempted (regardless of success/failure), exit the node
		// This allows users to force exit even if validation failed
		setWizardMode(false);
		setTimeout(() => setCurrentStep(1), 100);

		// Reset validation state for next time
		setValidationAttempted(false);
		setValidationFailed(false);

		onClose?.();
	}, [validateCurrentStep, validationAttempted, onClose]);

	// Check if we can proceed to next step - memoized to prevent re-renders
	const canProceedToNext = useMemo(() => {
		return currentStep < WIZARD_STEPS.length;
	}, [currentStep]);

	// Update wizard state for parent component
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: WIZARD_STEPS.length,
				canProceedToNext,
				onPreviousStep: handlePreviousStep,
				onNextStep: handleNextStep,
				onCompleteWizard: handleCompleteWizard,
				validationAttempted,
				validationFailed,
			});
		}
	}, [
		wizardMode,
		currentStep,
		canProceedToNext,
		handlePreviousStep,
		handleNextStep,
		handleCompleteWizard,
		validationAttempted,
		validationFailed,
		onWizardStateChange,
	]);

	// Provide validation function to parent - disabled to prevent validation loops
	// The wizard handles its own validation internally
	useEffect(() => {
		// Temporarily disabled to stop validation loops
		// if (onValidationRequest) {
		//   onValidationRequest(validateCurrentStep);
		// }
	}, [onValidationRequest]);

	const updateConfig = useCallback(
		(updates: Partial<SlackWizardConfig>) => {
			setConfig(prev => {
				const newConfig = { ...prev, ...updates };
				// Only update parent if this is a meaningful change
				if (JSON.stringify(prev) !== JSON.stringify(newConfig)) {
					updateParentData(newConfig);
				}
				return newConfig;
			});
		},
		[updateParentData]
	);

	const renderCurrentStep = () => {
		switch (currentStep) {
			case 1:
				return (
					<ConnectTargetStep
						nodeId={nodeId}
						config={config}
						onChange={updateConfig}
						errors={validationState.errors}
					/>
				);
			case 2:
				return (
					<MessageFormatStep
						nodeId={nodeId}
						config={config}
						onChange={updateConfig}
						errors={validationState.errors}
					/>
				);
			case 3:
				return (
					<PreviewStep
						nodeId={nodeId}
						config={config}
						onChange={updateConfig}
						errors={validationState.errors}
					/>
				);
			case 4:
				return (
					<SendStep
						nodeId={nodeId}
						config={config}
						onChange={updateConfig}
						errors={validationState.errors}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div className="flex flex-col h-full">
			{/* Wizard Header */}
			<div className="flex-shrink-0 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800">
				<div className="px-6 py-4">
					<div className="flex justify-between items-center mb-4">
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
							Slack Integration Setup
						</h2>
						<div className="text-sm text-gray-500 dark:text-gray-400">
							Step {currentStep} of {WIZARD_STEPS.length}
						</div>
					</div>

					{/* Step Progress */}
					<div className="flex items-center space-x-4">
						{WIZARD_STEPS.map((step, index) => {
							const Icon = step.icon;
							const isActive = step.id === currentStep;
							const isCompleted = step.id < currentStep;
							const isAccessible = step.id <= currentStep;

							return (
								<div key={step.id} className="flex items-center">
									<div
										className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
											isCompleted
												? 'text-white bg-green-500 border-green-500'
												: isActive
												? 'text-white bg-blue-500 border-blue-500'
												: isAccessible
												? 'text-gray-400 border-gray-300 dark:border-gray-600 dark:text-gray-500'
												: 'text-gray-300 border-gray-200 dark:border-gray-700 dark:text-gray-600'
										}`}
									>
										{isCompleted ? (
											<CheckCircle className="w-4 h-4" />
										) : (
											<Icon className="w-4 h-4" />
										)}
									</div>
									<div className="ml-2">
										<div
											className={`text-sm font-medium ${
												isActive
													? 'text-blue-600 dark:text-blue-400'
													: isCompleted
													? 'text-green-600 dark:text-green-400'
													: 'text-gray-500 dark:text-gray-400'
											}`}
										>
											{step.title}
										</div>
									</div>
									{index < WIZARD_STEPS.length - 1 && (
										<ArrowRight className="mx-3 w-4 h-4 text-gray-300 dark:text-gray-600" />
									)}
								</div>
							);
						})}
					</div>
				</div>
			</div>

			{/* Step Content */}
			<div className="overflow-y-auto flex-1">
				<div className="p-6">{renderCurrentStep()}</div>
			</div>

			{/* Error Display - Show validation errors if not handled by parent */}
			{!onWizardStateChange &&
				validationState.failed &&
				validationState.errors.length > 0 && (
					<div className="flex-shrink-0 bg-red-50 border-t border-gray-200 dark:border-gray-700 dark:bg-red-900/20">
						<div className="px-6 py-4">
							<div className="text-sm text-red-600 dark:text-red-400">
								{validationState.errors.join(', ')}
							</div>
						</div>
					</div>
				)}
		</div>
	);
}
